"use client"

import { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts"

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

const PaymentRecoveryAnalytics = () => {
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState(0)
  const [timeRange, setTimeRange] = useState("30")

  // Sample data for charts
  const recoveryRateData = [
    { name: "1st Attempt", success: 65, failure: 35 },
    { name: "2nd Attempt", success: 45, failure: 55 },
    { name: "3rd Attempt", success: 30, failure: 70 },
    { name: "Grace Period", success: 15, failure: 85 },
  ]

  const revenueRecoveredData = [
    { name: "Jan", amount: 4000 },
    { name: "Feb", amount: 3000 },
    { name: "Mar", amount: 5000 },
    { name: "Apr", amount: 4500 },
    { name: "May", amount: 6000 },
    { name: "Jun", amount: 5500 },
  ]

  const failureReasonData = [
    { name: "Insufficient Funds", count: 120 },
    { name: "Card Expired", count: 85 },
    { name: "Card Declined", count: 65 },
    { name: "Network Error", count: 45 },
    { name: "Other", count: 30 },
  ]

  const dunningPerformanceData = [
    { day: 1, recovered: 35 },
    { day: 2, recovered: 25 },
    { day: 3, recovered: 15 },
    { day: 5, recovered: 10 },
    { day: 7, recovered: 8 },
    { day: 10, recovered: 5 },
    { day: 14, recovered: 2 },
  ]

  useEffect(() => {
    // Simulate loading data
    const timer = setTimeout(() => {
      setLoading(false)
    }, 1000)

    return () => clearTimeout(timer)
  }, [])

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value)
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4">Payment Recovery Analytics</Typography>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="time-range-label">Time Range</InputLabel>
          <Select
            labelId="time-range-label"
            id="time-range"
            value={timeRange}
            label="Time Range"
            onChange={handleTimeRangeChange}
          >
            <MenuItem value="7">Last 7 days</MenuItem>
            <MenuItem value="30">Last 30 days</MenuItem>
            <MenuItem value="90">Last 90 days</MenuItem>
            <MenuItem value="365">Last year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Total Failed Payments
              </Typography>
              <Typography variant="h4">345</Typography>
              <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                -12% from last period
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Recovered Payments
              </Typography>
              <Typography variant="h4">187</Typography>
              <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                +8% from last period
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Recovery Rate
              </Typography>
              <Typography variant="h4">54.2%</Typography>
              <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                +5.3% from last period
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Revenue Recovered
              </Typography>
              <Typography variant="h4">$24,680</Typography>
              <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                +15% from last period
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Recovery Rate" />
          <Tab label="Revenue Recovered" />
          <Tab label="Failure Reasons" />
          <Tab label="Dunning Performance" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          <TabPanel value={activeTab} index={0}>
            <Typography variant="h6" gutterBottom>
              Recovery Rate by Attempt
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Percentage of payments recovered at each retry attempt
            </Typography>
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={recoveryRateData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="success" stackId="a" fill="#4caf50" name="Recovered" />
                  <Bar dataKey="failure" stackId="a" fill="#f44336" name="Failed" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Typography variant="h6" gutterBottom>
              Revenue Recovered Over Time
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Amount of revenue recovered through dunning process
            </Typography>
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueRecoveredData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`$${value}`, "Amount"]} />
                  <Legend />
                  <Line type="monotone" dataKey="amount" stroke="#3f51b5" name="Revenue Recovered" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <Typography variant="h6" gutterBottom>
              Payment Failure Reasons
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Distribution of reasons for payment failures
            </Typography>
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={failureReasonData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#ff9800" name="Number of Failures" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <Typography variant="h6" gutterBottom>
              Dunning Performance by Day
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Percentage of payments recovered by days since failure
            </Typography>
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dunningPerformanceData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="day"
                    label={{ value: "Days Since Failure", position: "insideBottomRight", offset: -10 }}
                  />
                  <YAxis label={{ value: "Recovery Rate (%)", angle: -90, position: "insideLeft" }} />
                  <Tooltip formatter={(value) => [`${value}%`, "Recovery Rate"]} />
                  <Legend />
                  <Line type="monotone" dataKey="recovered" stroke="#2196f3" name="Recovery Rate" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  )
}

export default PaymentRecoveryAnalytics

